// src/components/admin/shared/RichTextEditor.js
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Camera,
  Code,
  Columns,
  Heading1,
  Heading2,
  Heading3,
  ImageIcon,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
  Table,
  Type,
  Underline,
  Youtube
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import axios from '../../../utils/axiosHandler';

const RichTextEditor = ({ initialContent = '', onChange, placeholder = 'Enter content here...' }) => {
  const [editorContent, setEditorContent] = useState(initialContent);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showSideBySide, setShowSideBySide] = useState(false);
  const [fontSizeMenu, setFontSizeMenu] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const imageInputRef = useRef(null);
  
  // Set initial content when it changes externally
  useEffect(() => {
    setEditorContent(initialContent);
  }, [initialContent]);
  
  const handleContentChange = (e) => {
    const content = e.target.value;
    setEditorContent(content);
    if (onChange) {
      onChange(content);
    }
  };
  
  // Function to insert formatting tags at cursor position
  const insertFormat = (startTag, endTag = '') => {
    const textarea = document.getElementById('rich-text-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = editorContent.substring(start, end);
    
    // If no text is selected, insert tags with cursor in between
    if (start === end) {
      const newContent = 
        editorContent.substring(0, start) + 
        startTag + endTag + 
        editorContent.substring(end);
      
      setEditorContent(newContent);
      if (onChange) onChange(newContent);
      
      // Set cursor position between tags
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + startTag.length;
        textarea.selectionEnd = start + startTag.length;
      }, 0);
    } else {
      // If text is selected, wrap it with tags
      const newContent = 
        editorContent.substring(0, start) + 
        startTag + selectedText + endTag + 
        editorContent.substring(end);
      
      setEditorContent(newContent);
      if (onChange) onChange(newContent);
      
      // Select the newly wrapped text
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + startTag.length;
        textarea.selectionEnd = start + startTag.length + selectedText.length;
      }, 0);
    }
  };
  
  const handleBoldClick = () => insertFormat('<strong>', '</strong>');
  const handleItalicClick = () => insertFormat('<em>', '</em>');
  const handleUnderlineClick = () => insertFormat('<u>', '</u>');
  const handleH1Click = () => insertFormat('<h1>', '</h1>');
  const handleH2Click = () => insertFormat('<h2>', '</h2>');
  const handleH3Click = () => insertFormat('<h3>', '</h3>');
  const handleUlClick = () => insertFormat('<ul>\n  <li>', '</li>\n</ul>');
  const handleOlClick = () => insertFormat('<ol>\n  <li>', '</li>\n</ol>');
  const handleCodeClick = () => insertFormat('<code>', '</code>');
  const handleQuoteClick = () => insertFormat('<blockquote>', '</blockquote>');
  const handleAlignLeftClick = () => insertFormat('<div style="text-align: left;">', '</div>');
  const handleAlignCenterClick = () => insertFormat('<div style="text-align: center;">', '</div>');
  const handleAlignRightClick = () => insertFormat('<div style="text-align: right;">', '</div>');
  
  const handleLinkClick = () => {
    const url = prompt('Enter URL:');
    if (url) {
      insertFormat(`<a href="${url}" target="_blank">`, '</a>');
    }
  };
  
  const handleImageClick = () => {
    // Toggle image uploader instead of prompt
    setShowImageUploader(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post('/api/blog/upload', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        formData
      });
      
      if (res.status !== 200) {
        const errorData = await res.data;
        throw new Error(errorData.error || 'Failed to upload file');
      }
      
      const data = await res.data;
      
      // Insert image tag at cursor position
      insertFormat(`<img src="${data.filePath}" alt="Uploaded image" class="blog-image" />`);
      
      // Close image uploader
      setShowImageUploader(false);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleTableClick = () => {
    insertFormat(`<table class="blog-table">
  <thead>
    <tr>
      <th>Header 1</th>
      <th>Header 2</th>
      <th>Header 3</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Cell 1</td>
      <td>Cell 2</td>
      <td>Cell 3</td>
    </tr>
    <tr>
      <td>Cell 4</td>
      <td>Cell 5</td>
      <td>Cell 6</td>
    </tr>
  </tbody>
</table>`);
  };

  const handleYoutubeClick = () => {
    const videoId = prompt('Enter YouTube video ID (e.g., dQw4w9WgXcQ):');
    if (videoId) {
      insertFormat(`<div class="video-container">
  <iframe 
    width="560" 
    height="315" 
    src="https://www.youtube.com/embed/${videoId}" 
    frameborder="0" 
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
    allowfullscreen>
  </iframe>
</div>`);
    }
  };

  const handleTwoColumnsClick = () => {
    insertFormat(`<div class="two-columns">
  <div class="column">
    <!-- Content for first column -->
    First column content goes here
  </div>
  <div class="column">
    <!-- Content for second column -->
    Second column content goes here
  </div>
</div>`);
  };

  const handleFontSizeChange = (size) => {
    insertFormat(`<span style="font-size: ${size}px;">`, '</span>');
    setFontSizeMenu(false);
  };
  
  return (
    <div className="border border-gray-300 rounded-md overflow-hidden">
      {/* Toolbar */}
      <div className="border-b border-gray-300 bg-gray-50 p-2 flex flex-wrap gap-1">
        <button 
          type="button" 
          onClick={handleBoldClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Bold"
        >
          <Bold className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleItalicClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Italic"
        >
          <Italic className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleUnderlineClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Underline"
        >
          <Underline className="h-5 w-5 text-gray-700" />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
        <div className="relative">
          <button 
            type="button" 
            onClick={() => setFontSizeMenu(!fontSizeMenu)}
            className="p-1 rounded hover:bg-gray-200 flex items-center"
            title="Font Size"
          >
            <Type className="h-5 w-5 text-gray-700" />
            <span className="ml-1 text-xs">▼</span>
          </button>
          {fontSizeMenu && (
            <div className="absolute top-full left-0 bg-white border border-gray-300 shadow-md rounded-md z-10 mt-1">
              {[12, 14, 16, 18, 20, 24, 28, 32, 36].map(size => (
                <button
                  key={size}
                  className="block w-full text-left px-3 py-1 text-sm hover:bg-gray-100"
                  onClick={() => handleFontSizeChange(size)}
                >
                  {size}px
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
        <button 
          type="button" 
          onClick={handleH1Click}
          className="p-1 rounded hover:bg-gray-200"
          title="Heading 1"
        >
          <Heading1 className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleH2Click}
          className="p-1 rounded hover:bg-gray-200"
          title="Heading 2"
        >
          <Heading2 className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleH3Click}
          className="p-1 rounded hover:bg-gray-200"
          title="Heading 3"
        >
          <Heading3 className="h-5 w-5 text-gray-700" />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
        <button 
          type="button" 
          onClick={handleUlClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Bullet List"
        >
          <List className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleOlClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Numbered List"
        >
          <ListOrdered className="h-5 w-5 text-gray-700" />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
        <button 
          type="button" 
          onClick={handleLinkClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Insert Link"
        >
          <Link className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleImageClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Upload Image"
        >
          <Camera className="h-5 w-5 text-gray-700" />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
        <button 
          type="button" 
          onClick={handleCodeClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Code"
        >
          <Code className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleQuoteClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Quote"
        >
          <Quote className="h-5 w-5 text-gray-700" />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
        <button 
          type="button" 
          onClick={handleAlignLeftClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Align Left"
        >
          <AlignLeft className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleAlignCenterClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Align Center"
        >
          <AlignCenter className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleAlignRightClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Align Right"
        >
          <AlignRight className="h-5 w-5 text-gray-700" />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
        <button 
          type="button" 
          onClick={handleTableClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Insert Table"
        >
          <Table className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleYoutubeClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Insert YouTube Video"
        >
          <Youtube className="h-5 w-5 text-gray-700" />
        </button>
        <button 
          type="button" 
          onClick={handleTwoColumnsClick}
          className="p-1 rounded hover:bg-gray-200"
          title="Two Columns Layout"
        >
          <Columns className="h-5 w-5 text-gray-700" />
        </button>
        
        <div className="ml-auto flex space-x-2">
          <button
            type="button"
            onClick={() => {
              setShowSideBySide(!showSideBySide);
              if (isPreviewMode) setIsPreviewMode(false);
            }}
            className={`px-3 py-1 rounded text-sm font-medium ${
              showSideBySide ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Split View
          </button>
          
          <button
            type="button"
            onClick={() => {
              setIsPreviewMode(!isPreviewMode);
              if (!isPreviewMode) setShowSideBySide(false);
            }}
            className={`px-3 py-1 rounded text-sm font-medium ${
              isPreviewMode ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            disabled={showSideBySide}
          >
            {isPreviewMode ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>
      
      {/* Image Uploader Modal */}
      {showImageUploader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Upload Image</h3>
            <div className="flex flex-col items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 rounded-md p-6">
              <ImageIcon className="h-12 w-12 text-gray-400 mb-3" />
              <p className="text-sm text-gray-500 mb-4">
                Click to select or drop an image file
              </p>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Select Image
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setShowImageUploader(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Editor/Preview Area */}
      {isPreviewMode ? (
        <div 
          className="p-4 min-h-[300px] max-h-[600px] overflow-y-auto blog-preview-content"
          dangerouslySetInnerHTML={{ __html: editorContent }}
        ></div>
      ) : showSideBySide ? (
        <div className="flex min-h-[300px] max-h-[600px]">
          <textarea
            id="rich-text-editor"
            value={editorContent}
            onChange={handleContentChange}
            placeholder={placeholder}
            className="w-1/2 p-4 overflow-y-auto focus:outline-none focus:ring-0 focus:border-0 border-r border-gray-300"
            rows="15"
          ></textarea>
          <div 
            className="w-1/2 p-4 overflow-y-auto blog-preview-content"
            dangerouslySetInnerHTML={{ __html: editorContent }}
          ></div>
        </div>
      ) : (
        <textarea
          id="rich-text-editor"
          value={editorContent}
          onChange={handleContentChange}
          placeholder={placeholder}
          className="w-full p-4 min-h-[300px] max-h-[600px] overflow-y-auto focus:outline-none focus:ring-0 focus:border-0"
          rows="15"
        ></textarea>
      )}

      {/* CSS for preview styling */}
      <style jsx="true">{`
        .blog-preview-content img {
          max-width: 100%;
          height: auto;
          margin: 1rem 0;
        }
        
        .blog-preview-content h1 {
          font-size: 1.75rem;
          font-weight: bold;
          margin: 1.5rem 0 1rem;
        }
        
        .blog-preview-content h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 1.25rem 0 0.75rem;
        }
        
        .blog-preview-content h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 1rem 0 0.5rem;
        }
        
        .blog-preview-content blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1rem;
          font-style: italic;
          color: #4b5563;
          margin: 1rem 0;
        }
        
        .blog-preview-content code {
          background-color: #f3f4f6;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: monospace;
        }
        
        .blog-preview-content ul, .blog-preview-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
        }
        
        .blog-preview-content ul li {
          list-style-type: disc;
          margin-bottom: 0.5rem;
        }
        
        .blog-preview-content ol li {
          list-style-type: decimal;
          margin-bottom: 0.5rem;
        }
        
        .blog-preview-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
        }
        
        .blog-preview-content table th, 
        .blog-preview-content table td {
          border: 1px solid #e5e7eb;
          padding: 0.5rem;
        }
        
        .blog-preview-content table th {
          background-color: #f9fafb;
          font-weight: bold;
        }
        
        .blog-preview-content .video-container {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          overflow: hidden;
          margin: 1.5rem 0;
        }
        
        .blog-preview-content .video-container iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }
        
        .blog-preview-content .two-columns {
          display: flex;
          gap: 1.5rem;
          margin: 1rem 0;
        }
        
        .blog-preview-content .two-columns .column {
          flex: 1;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;






// // src/components/admin/shared/RichTextEditor.js
// import React, { useState, useEffect } from 'react';
// import { 
//   Bold, 
//   Italic, 
//   Underline, 
//   List, 
//   ListOrdered, 
//   Link, 
//   Image, 
//   Heading1, 
//   Heading2,
//   Heading3,
//   Code,
//   Quote,
//   AlignLeft,
//   AlignCenter,
//   AlignRight
// } from 'lucide-react';

// const RichTextEditor = ({ initialContent = '', onChange, placeholder = 'Enter content here...' }) => {
//   const [editorContent, setEditorContent] = useState(initialContent);
//   const [isPreviewMode, setIsPreviewMode] = useState(false);
  
//   // Set initial content when it changes externally
//   useEffect(() => {
//     setEditorContent(initialContent);
//   }, [initialContent]);
  
//   const handleContentChange = (e) => {
//     const content = e.target.value;
//     setEditorContent(content);
//     if (onChange) {
//       onChange(content);
//     }
//   };
  
//   // Function to insert formatting tags at cursor position
//   const insertFormat = (startTag, endTag = '') => {
//     const textarea = document.getElementById('rich-text-editor');
//     if (!textarea) return;
    
//     const start = textarea.selectionStart;
//     const end = textarea.selectionEnd;
//     const selectedText = editorContent.substring(start, end);
    
//     // If no text is selected, insert tags with cursor in between
//     if (start === end) {
//       const newContent = 
//         editorContent.substring(0, start) + 
//         startTag + endTag + 
//         editorContent.substring(end);
      
//       setEditorContent(newContent);
//       if (onChange) onChange(newContent);
      
//       // Set cursor position between tags
//       setTimeout(() => {
//         textarea.focus();
//         textarea.selectionStart = start + startTag.length;
//         textarea.selectionEnd = start + startTag.length;
//       }, 0);
//     } else {
//       // If text is selected, wrap it with tags
//       const newContent = 
//         editorContent.substring(0, start) + 
//         startTag + selectedText + endTag + 
//         editorContent.substring(end);
      
//       setEditorContent(newContent);
//       if (onChange) onChange(newContent);
      
//       // Select the newly wrapped text
//       setTimeout(() => {
//         textarea.focus();
//         textarea.selectionStart = start + startTag.length;
//         textarea.selectionEnd = start + startTag.length + selectedText.length;
//       }, 0);
//     }
//   };
  
//   const handleBoldClick = () => insertFormat('<strong>', '</strong>');
//   const handleItalicClick = () => insertFormat('<em>', '</em>');
//   const handleUnderlineClick = () => insertFormat('<u>', '</u>');
//   const handleH1Click = () => insertFormat('<h1>', '</h1>');
//   const handleH2Click = () => insertFormat('<h2>', '</h2>');
//   const handleH3Click = () => insertFormat('<h3>', '</h3>');
//   const handleUlClick = () => insertFormat('<ul>\n  <li>', '</li>\n</ul>');
//   const handleOlClick = () => insertFormat('<ol>\n  <li>', '</li>\n</ol>');
//   const handleCodeClick = () => insertFormat('<code>', '</code>');
//   const handleQuoteClick = () => insertFormat('<blockquote>', '</blockquote>');
//   const handleAlignLeftClick = () => insertFormat('<div style="text-align: left;">', '</div>');
//   const handleAlignCenterClick = () => insertFormat('<div style="text-align: center;">', '</div>');
//   const handleAlignRightClick = () => insertFormat('<div style="text-align: right;">', '</div>');
  
//   const handleLinkClick = () => {
//     const url = prompt('Enter URL:');
//     if (url) {
//       insertFormat(`<a href="${url}" target="_blank">`, '</a>');
//     }
//   };
  
//   const handleImageClick = () => {
//     const url = prompt('Enter image URL:');
//     if (url) {
//       insertFormat(`<img src="${url}" alt="Image" />`);
//     }
//   };
  
//   return (
//     <div className="border border-gray-300 rounded-md overflow-hidden">
//       {/* Toolbar */}
//       <div className="border-b border-gray-300 bg-gray-50 p-2 flex flex-wrap gap-1">
//         <button 
//           type="button" 
//           onClick={handleBoldClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Bold"
//         >
//           <Bold className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleItalicClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Italic"
//         >
//           <Italic className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleUnderlineClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Underline"
//         >
//           <Underline className="h-5 w-5 text-gray-700" />
//         </button>
        
//         <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
//         <button 
//           type="button" 
//           onClick={handleH1Click}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Heading 1"
//         >
//           <Heading1 className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleH2Click}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Heading 2"
//         >
//           <Heading2 className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleH3Click}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Heading 3"
//         >
//           <Heading3 className="h-5 w-5 text-gray-700" />
//         </button>
        
//         <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
//         <button 
//           type="button" 
//           onClick={handleUlClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Bullet List"
//         >
//           <List className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleOlClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Numbered List"
//         >
//           <ListOrdered className="h-5 w-5 text-gray-700" />
//         </button>
        
//         <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
//         <button 
//           type="button" 
//           onClick={handleLinkClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Insert Link"
//         >
//           <Link className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleImageClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Insert Image"
//         >
//           <Image className="h-5 w-5 text-gray-700" />
//         </button>
        
//         <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
//         <button 
//           type="button" 
//           onClick={handleCodeClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Code"
//         >
//           <Code className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleQuoteClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Quote"
//         >
//           <Quote className="h-5 w-5 text-gray-700" />
//         </button>
        
//         <div className="w-px h-6 bg-gray-300 mx-1"></div>
        
//         <button 
//           type="button" 
//           onClick={handleAlignLeftClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Align Left"
//         >
//           <AlignLeft className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleAlignCenterClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Align Center"
//         >
//           <AlignCenter className="h-5 w-5 text-gray-700" />
//         </button>
//         <button 
//           type="button" 
//           onClick={handleAlignRightClick}
//           className="p-1 rounded hover:bg-gray-200"
//           title="Align Right"
//         >
//           <AlignRight className="h-5 w-5 text-gray-700" />
//         </button>
        
//         <div className="ml-auto">
//           <button
//             type="button"
//             onClick={() => setIsPreviewMode(!isPreviewMode)}
//             className={`px-3 py-1 rounded text-sm font-medium ${
//               isPreviewMode ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
//             }`}
//           >
//             {isPreviewMode ? 'Edit' : 'Preview'}
//           </button>
//         </div>
//       </div>
      
//       {/* Editor/Preview Area */}
//       {isPreviewMode ? (
//         <div 
//           className="p-4 min-h-[300px] max-h-[600px] overflow-y-auto"
//           dangerouslySetInnerHTML={{ __html: editorContent }}
//         ></div>
//       ) : (
//         <textarea
//           id="rich-text-editor"
//           value={editorContent}
//           onChange={handleContentChange}
//           placeholder={placeholder}
//           className="w-full p-4 min-h-[300px] max-h-[600px] overflow-y-auto focus:outline-none focus:ring-0 focus:border-0"
//           rows="15"
//         ></textarea>
//       )}
//     </div>
//   );
// };

// export default RichTextEditor;